"use client";

import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { prefetchBookingAvailability } from "@/lib/booking-availability-client";
import {
  getBookCallPopupCopy,
  parseBookCallPopupVariant,
  type BookCallFormPrefill,
  type BookCallPopupCopy,
  type BookCallPopupVariant,
} from "@/lib/book-call-popup-copy";
import {
  bookCallOriginFromElement,
  bookCallVariantFromElement,
  defaultBookCallPopupOrigin,
  type BookCallPopupOrigin,
} from "@/lib/integrations/book-call-popup-origin";
import type { Lang } from "@/lib/types";
import { emitAnalyticsEvent } from "@/lib/analytics-events";

const CcNativeBookCallPopup = lazy(() =>
  import("@/components/booking/CcNativeBookCallPopup").then((module) => ({
    default: module.CcNativeBookCallPopup,
  })),
);

type BookCallOpenOptions = {
  variant?: BookCallPopupVariant;
  prefill?: BookCallFormPrefill;
};

type BookCallPopupContextValue = {
  open: (origin?: BookCallPopupOrigin, options?: BookCallOpenOptions) => void;
  popupCopy: BookCallPopupCopy;
};

const BookCallPopupContext = createContext<BookCallPopupContextValue | null>(
  null,
);

export function useBookCallPopup(): BookCallPopupContextValue {
  const ctx = useContext(BookCallPopupContext);
  if (!ctx) {
    throw new Error("useBookCallPopup must be used within CcBookCallPopupProvider");
  }
  return ctx;
}

export function useBookCallPopupOptional(): BookCallPopupContextValue | null {
  return useContext(BookCallPopupContext);
}

export function CcBookCallPopupProvider({
  children,
  lang = "en",
}: {
  children: ReactNode;
  lang?: Lang;
}) {
  const [nativeOpen, setNativeOpen] = useState(false);
  const [nativeOrigin, setNativeOrigin] = useState<BookCallPopupOrigin | null>(
    null,
  );
  const [popupVariant, setPopupVariant] =
    useState<BookCallPopupVariant>("default");
  const [nativePrefill, setNativePrefill] = useState<BookCallFormPrefill | null>(
    null,
  );
  const nativeTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (nativeOpen) emitAnalyticsEvent("site_booking_open", { booking_variant: popupVariant, language: lang });
  }, [nativeOpen, popupVariant, lang]);

  const popupCopy = useMemo(
    () => getBookCallPopupCopy(lang, popupVariant),
    [lang, popupVariant],
  );

  const open = useCallback(
    (origin?: BookCallPopupOrigin, options?: BookCallOpenOptions) => {
      setPopupVariant(options?.variant ?? "default");
      setNativePrefill(options?.prefill ?? null);
      setNativeOrigin(origin ?? defaultBookCallPopupOrigin());
      setNativeOpen(true);
    },
    [],
  );

  const closeNative = useCallback(() => {
    setNativeOpen(false);
    setNativePrefill(null);
    nativeTriggerRef.current?.removeAttribute("data-book-call-active");
    nativeTriggerRef.current = null;
  }, []);

  const prepareNativeClose = useCallback(() => {
    if (nativeTriggerRef.current?.isConnected) {
      setNativeOrigin(bookCallOriginFromElement(nativeTriggerRef.current));
    }
  }, []);

  useEffect(() => {
    const onBookingIntent = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-book-call-trigger="native"]')
      ) {
        prefetchBookingAvailability(lang);
      }
    };

    const onNativeTriggerClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest<HTMLElement>(
        '[data-book-call-trigger="native"]',
      );
      if (!trigger) return;
      nativeTriggerRef.current?.removeAttribute("data-book-call-active");
      nativeTriggerRef.current = trigger;
      trigger.setAttribute("data-book-call-active", "true");
      setPopupVariant(
        parseBookCallPopupVariant(bookCallVariantFromElement(trigger)),
      );
      setNativePrefill(null);
      setNativeOrigin(bookCallOriginFromElement(trigger));
      setNativeOpen(true);
    };
    document.addEventListener("pointerover", onBookingIntent, { passive: true });
    document.addEventListener("focusin", onBookingIntent);
    document.addEventListener("touchstart", onBookingIntent, { passive: true });
    document.addEventListener("click", onNativeTriggerClick);
    return () => {
      document.removeEventListener("pointerover", onBookingIntent);
      document.removeEventListener("focusin", onBookingIntent);
      document.removeEventListener("touchstart", onBookingIntent);
      document.removeEventListener("click", onNativeTriggerClick);
      nativeTriggerRef.current?.removeAttribute("data-book-call-active");
    };
  }, [lang]);

  const value = useMemo(
    () => ({
      open,
      popupCopy,
    }),
    [open, popupCopy],
  );

  return (
    <BookCallPopupContext.Provider value={value}>
      {children}
      {nativeOrigin ? (
        <Suspense fallback={null}>
          <CcNativeBookCallPopup
            open={nativeOpen}
            origin={nativeOrigin ?? defaultBookCallPopupOrigin()}
            lang={lang}
            popupCopy={popupCopy}
            bookingVariant={popupVariant}
            formPrefill={nativePrefill}
            onBeforeClose={prepareNativeClose}
            onClose={closeNative}
          />
        </Suspense>
      ) : null}
    </BookCallPopupContext.Provider>
  );
}
